import { z } from "zod";
import { regiaoDaFace, regiaoEspelhada } from "./regioes";
import { VISTAS_DA_FACE, vistaEspelhada } from "./vistas";

// Marcação do modo Face: ponto, área (círculo), elipse ou trajeto. Posições normalizadas no quadro
// do desenho (0–1); raio e semieixos são frações da largura. O tamanho é só desenho — nunca dose.

export const TIPOS_DE_MARCACAO = ["PONTO", "AREA", "ELIPSE", "TRAJETO"] as const;
export type TipoDeMarcacao = (typeof TIPOS_DE_MARCACAO)[number];

export const ROTULO_DO_TIPO_DE_MARCACAO: Record<TipoDeMarcacao, string> = {
  PONTO: "Ponto",
  AREA: "Área",
  ELIPSE: "Elipse",
  TRAJETO: "Trajeto",
};

/** Tamanhos rápidos (fração da largura) — raio da área, semieixo maior da elipse. */
export const TAMANHOS_DA_MARCACAO = { P: 0.025, M: 0.045, G: 0.075 } as const;
export type TamanhoDaMarcacao = keyof typeof TAMANHOS_DA_MARCACAO;
export const RAIO_DO_PONTO = 0.011;

const coord = z.number().min(0).max(1);
const medida = z.number().min(0.005).max(0.5);
const comum = {
  vista: z.enum(VISTAS_DA_FACE),
  regiao: z.string().max(40).nullish(),
  quantidade: z
    .number()
    .min(0)
    .max(99999)
    .transform((v) => Math.round(v * 100) / 100)
    .nullish(),
  observacao: z.string().trim().max(200).nullish(),
};

export const marcacaoFacialSchema = z.discriminatedUnion("tipo", [
  z.object({ tipo: z.literal("PONTO"), x: coord, y: coord, ...comum }),
  z.object({ tipo: z.literal("AREA"), x: coord, y: coord, raio: medida, ...comum }),
  z.object({ tipo: z.literal("ELIPSE"), x: coord, y: coord, rx: medida, ry: medida, rotacao: z.number().min(-180).max(180), ...comum }),
  z.object({ tipo: z.literal("TRAJETO"), pontos: z.array(z.tuple([coord, coord])).min(2).max(20), ...comum }),
]);

export type MarcacaoFacial = z.infer<typeof marcacaoFacialSchema>;

/** Cópia independente no lado oposto: posição espelhada, vista e região do par. */
export function espelharMarcacao(m: MarcacaoFacial): MarcacaoFacial {
  const base = { ...m, vista: vistaEspelhada(m.vista), regiao: regiaoEspelhada(m.regiao) ?? m.regiao ?? null };
  switch (m.tipo) {
    case "TRAJETO":
      return { ...base, tipo: "TRAJETO", pontos: m.pontos.map(([x, y]): [number, number] => [1 - x, y]) };
    case "ELIPSE":
      return { ...base, tipo: "ELIPSE", x: 1 - m.x, y: m.y, rx: m.rx, ry: m.ry, rotacao: -m.rotacao };
    case "AREA":
      return { ...base, tipo: "AREA", x: 1 - m.x, y: m.y, raio: m.raio };
    default:
      return { ...base, tipo: "PONTO", x: 1 - m.x, y: m.y };
  }
}

/** Centro da marcação (o trajeto usa o primeiro ponto) — onde a região é sugerida e o rótulo fica. */
export const centroDaMarcacao = (m: MarcacaoFacial): [number, number] => (m.tipo === "TRAJETO" ? m.pontos[0] : [m.x, m.y]);

const numero = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 2 });
export const formatarQuantidade = (q: number, unidade?: string | null) => `${numero.format(q)}${unidade ? ` ${unidade}` : ""}`;

/** Regiões citadas, juntando os pares ("Malar D/E"), na ordem em que aparecem. */
export function regioesDoResumo(marcacoes: Pick<MarcacaoFacial, "regiao">[]): string[] {
  const bases: { base: string; nome: string; lados: Set<string> }[] = [];
  for (const m of marcacoes) {
    const r = regiaoDaFace(m.regiao);
    if (!r) continue;
    let b = bases.find((x) => x.base === r.base);
    if (!b) bases.push((b = { base: r.base, nome: r.nome, lados: new Set() }));
    if (r.lado !== "C") b.lados.add(r.lado);
  }
  return bases.map((b) => (b.lados.size === 0 ? b.nome : `${b.nome} ${["D", "E"].filter((l) => b.lados.has(l)).join("/")}`));
}

/** "Glabela, Pés de galinha D/E · 24 U em 5 pontos". Quantidade só soma as marcações que têm. */
export function resumoDaFace(marcacoes: Pick<MarcacaoFacial, "regiao" | "tipo" | "quantidade">[], unidade?: string | null): string {
  if (marcacoes.length === 0) return "";
  const n = marcacoes.length;
  const soPontos = marcacoes.every((m) => m.tipo === "PONTO");
  const nome = (k: number) => (soPontos ? (k === 1 ? "ponto" : "pontos") : k === 1 ? "marcação" : "marcações");
  const comQuantidade = marcacoes.filter((m) => m.quantidade != null && m.quantidade > 0);
  const total = comQuantidade.reduce((s, m) => s + (m.quantidade ?? 0), 0);
  let contagem = `${n} ${nome(n)}`;
  if (comQuantidade.length === n) contagem = `${formatarQuantidade(total, unidade)} em ${n} ${nome(n)}`;
  else if (comQuantidade.length > 0) contagem = `${formatarQuantidade(total, unidade)} em ${comQuantidade.length} de ${n} ${nome(n)}`;
  const regioes = regioesDoResumo(marcacoes);
  return regioes.length ? `${regioes.join(", ")} · ${contagem}` : contagem;
}
